<template>
  <v-card flat>
    <v-card-title class="text-h5">
      Deforestation rate of planned deforestation
    </v-card-title>
    <v-card-subtitle>
      Planned deforestation is defined as the deforestation on forest lands that are legally authorized and documented to be converted to non‐forest land (Infrastructure projects, roads, mining concessions).
      <br/>This module computes the annual average deforestation rate within the areas considered as “Planned Deforestation".
      <br/>This information is used to estimate the future planned deforestation over the concession areas.
    </v-card-subtitle>

    <v-card-text>
      <v-row>
        <!-- Input form -->
        <v-col>
          <v-card elevation="4" :disabled="finishedProcessing">
            <v-card-text>
              <v-form v-model="isValid">
                  <p class="text-body-1">Add a tiff object with concession area (at least one)</p>
                  <v-row>
                    <v-col>
                      <v-select v-model="concLayer1" :items="concessionLayers" item-text="NOMBRE" return-object label="Concessions layer 1" prepend-icon="mdi-file" outlined dense :rules="[globalRules.required]"/>
                    </v-col>
                    <v-col>
                      <v-select v-model="concLayer2" :disabled="!concLayer1" :items="concessionLayers" item-text="NOMBRE" return-object label="Concessions layer 2" prepend-icon="mdi-file" outlined dense/>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <v-select v-model="concLayer3" :disabled="!concLayer2" :items="concessionLayers" item-text="NOMBRE" return-object label="Concessions layer 3" prepend-icon="mdi-file" outlined dense/>
                    </v-col>
                    <v-col>
                      <v-select v-model="concLayer4" :disabled="!concLayer3" :items="concessionLayers" item-text="NOMBRE" return-object label="Concessions layer 4" prepend-icon="mdi-file" outlined dense/>
                    </v-col>
                  </v-row>

                  <v-checkbox v-model="addAdditionalLayer" label="Add an additional layer when the deforestation is planned and annual deforestation rate is estimated."></v-checkbox>
                  <v-expand-transition>
                    <v-row v-show="addAdditionalLayer">
                      <v-col>
                        <v-select v-model="additionalLayer" :items="concessionLayers" label="File type" prepend-icon="mdi-file" outlined dense/>
                      </v-col>
                      <v-col>
                        <v-text-field v-model="annualDefRate" class="right-input" label="Annual Def. Rate" outlined dense type="number" :rules="annualDefRateRules"/>
                      </v-col>
                    </v-row>
                  </v-expand-transition>
              </v-form>

              <v-btn width="150" color="primary" :disabled="!isValid" :loading="isInitProcessing" @click="process">Process</v-btn>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Results -->
        <v-col>
          <v-card elevation="4">
            <v-card-title>The deforestation rates estimated for the selected variables are the following:</v-card-title>
            <v-card-text>
              <v-simple-table>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Variable</th>
                      <th class="text-left">Deforestation Rate</th>
                      <th class="text-left">Forest Area</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in mod1Result" :key="item.variable">
                      <td>{{ item.variable }}</td>
                      <td>{{ item.defRate }} %</td>
                      <td>{{ item.forestArea }} ha</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <!-- Mixed -->
              <v-row justify="center">
                <v-col md="3">
                  <v-text-field v-model="defRateMixed" readonly class="mt-2" label="DefRate_Mixed" suffix="%" filled dense type="decimal"/>
                </v-col>
                <v-col md="3">
                  <v-text-field v-model="forestAreaMixed" readonly class="mt-2" label="ForestArea_Mixed" suffix="ha" filled dense type="decimal"/>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex'
import api from '../../api'

export default {
  name: 'Module1',

  props: {
    region: Object,
    currentProcess: String
  },

  data: () => ({
    isValid: false,
    annualDefRateRules: [
      v => !!v || 'Required.',
      v => (v && v >= 0) || 'Should be at least 0',
      v => (v && v <= 100) || 'Should not be above 100'
    ],

    // Periodic checking
    isInitProcessing: false,
    isProcessing: false,
    intervalCall: null,
    finishedProcessing: false,

    // Inputs
    concLayer1: null,
    concLayer2: null,
    concLayer3: null,
    concLayer4: null,
    annualDefRate: 10,
    addAdditionalLayer: false,
    additionalLayer: null,

    // Outputs
    mod1Result: [],
    defRateMixed: null,
    forestAreaMixed: null
  }),

  computed: {
    concessionLayers () {
      return this.$store.getters['file/filesByRegionAndType'](this.region, 3) // 3 == Concession layer
    }
  },

  watch: {
    isProcessing (val) {
      this.isInitProcessing = val

      if (val && !this.intervalCall) {
        this.intervalCall = setInterval(() => {
          api.process.getResults(this.currentProcess, 1).then(response => {
            if (response.data.error) {
              this.stopProcessing()

              console.error(response.data.error)
              this.$toastr.e(response.data.message)
            } else {
              if (response.data.finished) {
                let results = response.data.results
                this.defRateMixed = results.defRateMixed
                this.forestAreaMixed = results.forestAreaMixed
                this.mod1Result = results.tabla

                this.$toastr.s('Module 1 succesfully processed')

                this.finishedProcessing = true
                this.$emit('onProcessed')

                this.stopProcessing()
              } else {
                // Not finished
              }
            }
          }).catch(error => {
            this.stopProcessing()

            console.error(error)
            this.$toastr.e('Error processing module 1')
          }).finally(() => {
            // Nothing
          })
        }, 10 * 1000)
      } else {
        clearInterval(this.intervalCall)
        this.intervalCall = null
      }
    }
  },

  methods: {
    ...mapActions('app', [
      'setIsProcessing'
    ]),

    stopProcessing () {
      this.isProcessing = false
      clearInterval(this.intervalCall)
      this.intervalCall = null

      this.setIsProcessing(false)
    },

    reset () {
      this.finishedProcessing = false

      this.mod1Result = []
      this.defRateMixed = null
      this.forestAreaMixed = null
    },

    async process () {
      let data = {
        processId: this.currentProcess,
        F1: !this.concLayer1 ? null : this.concLayer1,
        F2: !this.concLayer2 ? null : this.concLayer2,
        F3: !this.concLayer3 ? null : this.concLayer3,
        F4: !this.concLayer4 ? null : this.concLayer4,
        F5: !this.additionalLayer ? null : this.additionalLayer,
        addAdditionalLayer: this.addAdditionalLayer,
        annualDefRate: this.annualDefRate
      }

      try {
        this.setIsProcessing(true)
        this.isInitProcessing = true

        await api.process.module1(data)

        this.isProcessing = true
        this.$toastr.s('Module 1 starts processing...')
      } catch (error) {
        this.isInitProcessing = false

        console.error(error)
        this.$toastr.e('Error initializing module 1')
      }
    }
  }
}
</script>

<style scoped>
  .right-input >>> input {
        text-align: right
  }
</style>
